import {OperationKey, ApiError} from "../../shared/api/client";
import type {ConsultationWizardAPI} from "./api";
import type {DraftOrder, DraftSelection} from "./model";
import type {BookingEntryPersistence} from "../../shared/session/pending-booking-entry";

export const bookingEntryPath = "/app/consultations/new";
export type BookingEntry = {kind: "generic"} | {kind: "invalid"} | {kind: "booking"; expertAccountId: number; href: string};

/** Navigation carries only public hints. Everything else is owner selection. */
export function parseBookingEntry(search: string): BookingEntry {
  if (!search || search === "?") return {kind: "generic"};
  const query = new URLSearchParams(search);
  const ids = query.getAll("expert_account_id");
  const modes = query.getAll("mode");
  if ([...query.keys()].some((key) => key !== "expert_account_id" && key !== "mode") ||
    ids.length !== 1 || modes.length !== 1 || modes[0] !== "booking_backed" || !/^[1-9][0-9]*$/.test(ids[0]!)) return {kind: "invalid"};
  const expertAccountId = Number(ids[0]);
  if (!Number.isSafeInteger(expertAccountId)) return {kind: "invalid"};
  return {kind: "booking", expertAccountId, href: `${bookingEntryPath}?expert_account_id=${expertAccountId}&mode=booking_backed`};
}

export function bookingReturnTo(value: string): string | null {
  if (!value.startsWith(`${bookingEntryPath}?`) || value.includes("#")) return null;
  const entry = parseBookingEntry(value.slice(bookingEntryPath.length));
  return entry.kind === "booking" ? entry.href : null;
}

export type BookingEntryAPI = Pick<ConsultationWizardAPI, "create" | "get" | "replace" | "resolveClientTimezone">;

/** Retain both keys and the original Replace body through an uncertain result. */
export class BookingEntryAttempt {
  private readonly createKey = new OperationKey();
  private replaceKey = new EntryReplaceKey();
  private created: DraftOrder | undefined;
  private selection: DraftSelection | undefined;
  private inFlight: Promise<string> | undefined;

  constructor(private readonly expertAccountId: number, private readonly api: BookingEntryAPI, private readonly guard: () => void, private readonly persistence?: BookingEntryPersistence) {}

  run(): Promise<string> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.execute().finally(() => { this.inFlight = undefined; });
    return this.inFlight;
  }

  resumeRoute(): string | undefined {
    return this.created ? draftRoute(this.created) : undefined;
  }

  private async execute(): Promise<string> {
    this.guard();
    if (!this.created) {
      const pending = this.persistence?.read(this.expertAccountId);
      if (pending) {
        this.selection = {expert_account_id: pending.expertAccountId, channel: "voice", mode: "booking_backed", client_timezone: pending.clientTimezone};
        this.replaceKey = new EntryReplaceKey(pending.replaceKey);
        const current = await this.api.get(pending.draftId);
        this.guard();
        this.created = current;
        if (current.state !== "draft" || Object.keys(current.selection ?? {}).length > 0) {
          this.persistence?.clear();
          return draftRoute(current);
        }
        if (current.version !== pending.expectedVersion) throw entryError(409, "conflict");
      }
    }
    // A missing zone fails before Create, without a guessed default.
    this.selection ??= {expert_account_id: this.expertAccountId, channel: "voice", mode: "booking_backed", client_timezone: this.api.resolveClientTimezone()};
    if (!this.created) {
      const result = await this.api.create(this.createKey);
      this.guard();
      if (result.outcome !== "applied") throw entryError(422, "feature_unavailable");
      this.created = result.draft;
    }
    // A replayed or already progressed Draft is resumed without overwriting it.
    if (this.created.state !== "draft" || Object.keys(this.created.selection ?? {}).length > 0) {
      this.persistence?.clear();
      return draftRoute(this.created);
    }
    if (!this.created.allowed_actions.includes("replace_selection")) throw entryError(409, "conflict");
    this.guard();
    this.persistence?.write({draftId: this.created.draft_id, expectedVersion: this.created.version, expertAccountId: this.expertAccountId, clientTimezone: this.selection.client_timezone!, replaceKey: this.replaceKey.value});
    const result = await this.api.replace(this.created, this.selection, this.replaceKey);
    this.guard();
    if (result.outcome !== "applied") throw entryError(422, "feature_unavailable");
    const selected = result.draft.selection;
    if (selected?.expert_account_id !== this.expertAccountId || selected.channel !== "voice" || selected.mode !== "booking_backed" || selected.client_timezone !== this.selection.client_timezone) throw entryError(502, "client_inconsistent_source");
    this.persistence?.clear();
    return draftRoute(result.draft);
  }
}

// This one key has one immutable selection/version, also when resumed on reload.
class EntryReplaceKey extends OperationKey {
  constructor(readonly value: string = crypto.randomUUID()) { super(); }
  override forPayload(): string { return this.value; }
}

export function draftRoute(draft: DraftOrder): string {
  return `/app/consultations/${encodeURIComponent(draft.draft_id)}`;
}

function entryError(status: number, code: string): ApiError {
  return new ApiError(status, {error: {code, message: "", request_id: "", fields: {}}});
}
